/**
 * DashboardResolverService — Cosmos-only meta resolver.
 *
 * Cosmos (`MetaDashboardCacheService`) is the single source of truth for
 * MetaDashboard documents. If a meta isn't in the cache, the resolver returns
 * a synthetic "not found" placeholder so the renderer surfaces an explicit
 * error instead of silently substituting stale local TypeScript constants.
 *
 * Callers may still pass a TS const for type compatibility with older code
 * paths; the parameter is preserved but ignored.
 */
import { Injectable, computed, inject } from '@angular/core';
import { MetaDashboard } from '../models/meta-dashboard.types';
import { MetaDashboardCacheService } from './meta-dashboard-cache.service';

@Injectable({ providedIn: 'root' })
export class DashboardResolverService {
    private readonly cache = inject(MetaDashboardCacheService);

    /**
     * Resolve a meta to render. Always reads from the Cosmos cache.
     * On cache miss, returns a "not found" placeholder.
     *
     * The `_tsConst` parameter is retained for backwards compatibility with
     * existing callers but is no longer consulted.
     */
    resolveMeta(metaId: string, _tsConst: MetaDashboard): MetaDashboard;
    resolveMeta(metaId: string, _tsConst: MetaDashboard | null): MetaDashboard | null;
    resolveMeta(metaId: string, _tsConst: MetaDashboard | null): MetaDashboard | null {
        return this.cache.map().get(metaId) ?? this.notFoundMeta(metaId);
    }

    /** Reactive variant — returns a computed signal so consumers can use [meta]="meta()". */
    metaSignal(metaId: string, tsConst: MetaDashboard) {
        return computed(() => this.resolveMeta(metaId, tsConst));
    }

    /** Resolve by id only (used by the generic `dashboards/:metaId` route). */
    resolveMetaById(metaId: string): MetaDashboard {
        return this.cache.map().get(metaId) ?? this.notFoundMeta(metaId);
    }

    /** Minimal placeholder meta. Surfaces the missing id so the user knows what's wrong. */
    private notFoundMeta(metaId: string): MetaDashboard {
        return {
            id:            metaId,
            schemaVersion: 2,
            title:         `Dashboard not found: ${metaId}`,
            icon:          'tma:practice-dashboard',
            description:   `No MetaDashboard registered in Cosmos for id "${metaId}". Reseed the global-reference container or check the nav structure.`,
            category:      'finance',
            navigation:    { title: metaId, icon: 'tma:practice-dashboard', order: 0 },
            permissions:   { roles: [] },
            aiContext:     '',
            defaults: {
                dateField: 'ServiceDate', dateFieldLabel: 'Service Date',
                datePreset: 'lastYear', dataEntity: 'encounter', dataEntityLabel: 'Visit',
                contextDisplay: { top: ['source'], bottom: [] },
            },
            tabDisplay: { hideWhenSingle: true },
            dashboards: [{
                id:     'main',
                label:  'Not Found',
                icon:   'tma:practice-dashboard',
                layout: { columns: 12, gap: '1rem', padding: '1rem' },
                widgets: [],
                default: true,
            }],
            widgets: {},
        };
    }
}
